import { getStripeAmountToCharge } from '../../utils/utils'
import { type CheckoutSummary } from './types/checkout.types'

export type StripeRow = { name: string; amount: number }

type TaxDetail = { name: string; amount: number }

type GiftCardCredit = { price: number }

type Translate = (key: string, options?: Record<string, string>) => string

type SheetFormat = {
  productBasis: 'net' | 'gross'
  showFreeTiles: boolean
  showFreeShipping: boolean
  taxRows: 'combined' | 'itemized'
  showCredits: boolean
}

// The payment request sheet: one product row with tax and credits already taken
// out of it, and a single tax line.
export const PAYMENT_REQUEST_FORMAT: SheetFormat = {
  productBasis: 'net',
  showFreeTiles: true,
  showFreeShipping: true,
  taxRows: 'combined',
  showCredits: false,
}

// The express element itemises: a gross subtotal, each tax by name, and credits
// and gift cards as their own negative rows.
export const EXPRESS_CHECKOUT_FORMAT: SheetFormat = {
  productBasis: 'gross',
  showFreeTiles: false,
  showFreeShipping: false,
  taxRows: 'itemized',
  showCredits: true,
}

export function toStripeSheetRows(
  translate: Translate,
  summary: CheckoutSummary,
  { productLabel, format }: { productLabel: string; format: SheetFormat }
): StripeRow[] {
  if (!summary?.currency) {
    return []
  }
  const toAmount = (totalCost: number) =>
    getStripeAmountToCharge({ totalCost, currency: summary.currency })
  const { totalTiles = 0, freeTiles = 0, shippingCost, tax = 0 } = summary
  const rows: StripeRow[] = []

  if (format.productBasis === 'gross') {
    rows.push({
      name: productLabel,
      amount: toAmount(summary.productTotalPrice),
    })
  } else if (totalTiles - freeTiles > 0) {
    rows.push({
      name: productLabel,
      amount: toAmount(summary.totalPrice - (shippingCost || 0) - tax),
    })
  }

  if (format.showFreeTiles && freeTiles) {
    rows.push({
      name:
        freeTiles > 1
          ? translate('order.checkout.payment_summary_rows.free_tiles_many', {
              v1: String(freeTiles),
            })
          : translate('order.checkout.payment_summary_rows.free_tiles_single'),
      amount: 0,
    })
  }

  if (shippingCost != null && (format.showFreeShipping || shippingCost > 0)) {
    const shipping = translate('order.checkout.payment_summary_rows.shipping')
    rows.push({
      name:
        shippingCost === 0
          ? `${translate(
              'order.checkout.payment_summary_rows.free'
            )} ${shipping}`
          : shipping,
      amount: toAmount(shippingCost),
    })
  }

  if (format.taxRows === 'combined') {
    if (tax) {
      rows.push({
        name: translate('order.checkout.payment_summary_rows.tax'),
        amount: toAmount(tax),
      })
    }
  } else {
    summary.taxesDetailed
      .filter((taxItem: TaxDetail) => taxItem.amount > 0)
      .forEach((taxItem: TaxDetail) =>
        rows.push({ name: taxItem.name, amount: toAmount(taxItem.amount) })
      )
  }

  if (format.showCredits) {
    if (summary.creditUsed > 0) {
      rows.push({
        name: translate('order.checkout.payment_summary_rows.creditUsed'),
        amount: -toAmount(summary.creditUsed),
      })
    }
    summary.giftCardCreditItems
      // Gift card summary rows already carry the deduction as a negative price.
      .filter((gift: GiftCardCredit) => gift.price < 0)
      .forEach((gift: GiftCardCredit) =>
        rows.push({
          name: translate('order.checkout.gift_card_credits'),
          amount: toAmount(gift.price),
        })
      )
  }

  return rows
}
